import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Dumbbell, Menu, X, LayoutDashboard, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../ui/LanguageSwitcher';
import { auth, db } from '../../config/firebase';
import { doc, getDoc } from 'firebase/firestore';

const PublicHeader = () => {
    const { t } = useTranslation('marketing');
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [user, setUser] = useState(auth.currentUser);
    const [profile, setProfile] = useState(null);

    useEffect(() => {
        const unsub = auth.onAuthStateChanged(async (u) => {
            setUser(u);
            if (u) {
                // Load profile from Firestore
                try {
                    const profileDoc = await getDoc(doc(db, 'users', u.uid));
                    if (profileDoc.exists()) {
                        setProfile(profileDoc.data());
                    }
                } catch (err) {
                    console.error('Error loading profile:', err);
                }
            } else {
                setProfile(null);
            }
        });
        return () => unsub();
    }, []);

    // Use profile data if available, otherwise fall back to auth data
    const displayName = profile?.username || profile?.displayName || user?.displayName?.split(' ')[0] || user?.email?.split('@')[0] || t('nav.user_default');
    const photoURL = profile?.profilePicture || user?.photoURL;

    const navLinks = [
        { href: '/landing', label: t('nav.home') },
        { href: '/exercises', label: t('nav.exercises') },
        { href: '/tools', label: t('nav.tools') },
        { href: '/resources', label: t('nav.resources') },
        { href: '/about', label: t('nav.about') },
        { href: '/contact', label: t('nav.contact') },
    ];

    return (
        <header className="sticky top-0 z-50 bg-black/95 backdrop-blur-md border-b border-neutral-800">
            <nav className="container mx-auto px-3 sm:px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))]">
                <div className="flex items-center justify-between gap-4">
                    {/* Logo */}
                    <a href="/" className="flex items-center gap-2 group flex-shrink-0">
                        <div className="-rotate-12 group-hover:scale-110 transition-transform transform-gpu">
                            <Dumbbell size={28} className="text-red-600 rotate-45 filter drop-shadow-[0_0_8px_rgba(220,38,38,0.5)]" strokeWidth={2.5} />
                        </div>
                        <span className="text-sm sm:text-lg font-black text-white uppercase italic tracking-wider inline">
                            GymTracker<span className="text-red-500">.pro</span>
                        </span>
                    </a>

                    {/* Desktop Navigation */}
                    <div className="hidden lg:flex items-center gap-4 flex-1 justify-center">
                        {navLinks.map((link) => {
                            const isActive = location.pathname === link.href || (link.href !== '/landing' && location.pathname.startsWith(link.href));
                            return (
                                <a
                                    key={link.href}
                                    href={link.href}
                                    className={`${isActive ? 'text-white' : 'text-neutral-400'} hover:text-white font-bold uppercase text-xs tracking-wider transition-colors whitespace-nowrap`}
                                >
                                    {link.label}
                                </a>
                            );
                        })}
                    </div>

                    {/* Right side: Language, Auth */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="hidden md:flex">
                            <LanguageSwitcher />
                        </div>

                        {user ? (
                            /* User is logged in - show compact user card + app button */
                            <div className="flex items-center gap-2">
                                <a
                                    href="/"
                                    className="hidden md:flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white font-bold text-xs uppercase tracking-wider rounded-lg transition-all"
                                >
                                    <LayoutDashboard size={14} />
                                    <span className="hidden lg:inline">{t('nav.go_to')}</span> {t('nav.app')}
                                </a>
                                {/* User Profile Badge - Mobile Friendly */}
                                <div className="flex items-center gap-2 pl-2">
                                    {photoURL ? (
                                        <img src={photoURL} alt={displayName} className="w-8 h-8 rounded-full object-cover ring-2 ring-neutral-800" />
                                    ) : (
                                        <div className="w-8 h-8 rounded-full bg-red-900/50 flex items-center justify-center ring-2 ring-red-900/30">
                                            <User size={14} className="text-red-500" />
                                        </div>
                                    )}
                                    <span className="text-white text-sm font-bold truncate max-w-[100px]">
                                        {displayName}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            /* User is NOT logged in - show login button */
                            <a
                                href={`/login?redirectTo=${encodeURIComponent(location.pathname)}`}
                                className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white font-bold uppercase text-xs tracking-wider rounded-lg transition-all"
                            >
                                {t('nav.login')}
                            </a>
                        )}
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        className="md:hidden p-2 text-white hover:bg-neutral-900 rounded-lg transition-colors"
                        aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                    >
                        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden mt-4 py-4 border-t border-neutral-800 space-y-4">
                        {navLinks.map((link) => {
                            const isActive = location.pathname === link.href || (link.href !== '/landing' && location.pathname.startsWith(link.href));
                            return (
                                <a
                                    key={link.href}
                                    href={link.href}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={`block ${isActive ? 'text-white' : 'text-neutral-400'} hover:text-white font-bold uppercase text-sm tracking-wider transition-colors`}
                                >
                                    {link.label}
                                </a>
                            );
                        })}
                        <div className="flex justify-center">
                            <LanguageSwitcher />
                        </div>

                        {user ? (
                            /* User logged in - mobile */
                            <div className="space-y-3">
                                <div className="flex items-center justify-center gap-2 py-2">
                                    {photoURL ? (
                                        <img src={photoURL} alt="" className="w-8 h-8 rounded-full object-cover" />
                                    ) : (
                                        <div className="w-8 h-8 rounded-full bg-red-900/50 flex items-center justify-center">
                                            <User size={16} className="text-red-500" />
                                        </div>
                                    )}
                                    <span className="text-white font-medium">
                                        {displayName}
                                    </span>
                                </div>
                                <a
                                    href="/"
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="flex items-center justify-center gap-2 px-6 py-2.5 bg-red-600 text-white font-bold uppercase tracking-wider rounded-lg"
                                >
                                    <LayoutDashboard size={18} />
                                    {t('nav.go_to_app')}
                                </a>
                            </div>
                        ) : (
                            <a
                                href="/login"
                                onClick={() => setMobileMenuOpen(false)}
                                className="block text-center px-6 py-2.5 bg-red-600 text-white font-bold uppercase tracking-wider rounded-lg transition-all"
                            >
                                {t('nav.login')}
                            </a>
                        )}
                    </div>
                )}
            </nav>
        </header>
    );
};

export default PublicHeader;

